import { useTranslation } from 'react-i18next'
import { ReactNode, forwardRef } from 'react'

import PlainIcon, { iconColor, iconType } from 'app/common/ui-next/plain-icon'
import PlainButtonAsLink from 'app/common/ui-next/plain-button-as-link/plain-button-as-link'
import { PlainConfirm } from 'app/common/ui-next/plain-confirm-modal'
import { BankCardId, CardIssuer, CardSystem } from 'app/dataTypes/acquiring/recurrent/cards'

import { CardSystemIcon } from './system/CardSystemIcon'
import { CardIssuerIcon } from './issuer/CardIssuerIcon'
import styles from './CardTemplate.module.scss';

type Props = {
  children: ReactNode
  label: string
  id: string
  groupName: string
  dashed?: boolean
  lastFourDigits?: string
  system?: CardSystem
  issuer?: CardIssuer
  checked?: boolean
  onTrashButtonClick?: (id: string) => Promise<boolean>
  onChangeValue?: (id: BankCardId) => void
}

export const CardTemplate = forwardRef<HTMLInputElement, Props>(
  (
    {
      children,
      label,
      id,
      groupName,
      dashed,
      lastFourDigits,
      system,
      issuer,
      checked,
      onTrashButtonClick,
      onChangeValue,
    },
    ref,
  ) => {
    const { t } = useTranslation('finances')

    const dashedProps = dashed ? { strokeDasharray: '7 7' } : {}

    const handleConfirmed = async () => {
      const res = await onTrashButtonClick?.(id)
      return !!res
    }

    return (
      <label className={styles.root}>
        <input
          type="radio"
          id={id}
          value={id}
          name={groupName}
          className={styles.input}
          checked={checked}
          onChange={() => onChangeValue?.(id)}
          ref={ref}
        />
        <div className={styles.card}>
          <svg
            className={styles.cardDashedBg}
            width="168"
            height="106"
            viewBox="0 0 168 106"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <rect
              x="1"
              y="1"
              width="166"
              height="104"
              rx="14"
              strokeWidth="2"
              strokeLinecap="round"
              {...dashedProps}
            />
          </svg>
          {children}
        </div>

        {lastFourDigits && <div className={styles.lastFourDigits}>{`** ${lastFourDigits}`}</div>}

        {onTrashButtonClick && (
          <PlainConfirm
            messageText={t('save_card_remove_confirm_message', 'Убрать карту из сохраненных?') ?? ''}
            confirmButtonTitle={t('save_card_remove_confirm_ok', 'Убрать') || undefined}
            confirmButtonTitlePending={
              t('save_card_remove_confirm_ok_pending', 'Подождите...') || undefined
            }
            onConfirm={handleConfirmed}
          >
            {(beginConfirmation) => (
              <PlainButtonAsLink onClick={beginConfirmation} className={styles.buttonTrash}>
                <PlainIcon
                  width="20"
                  height="auto"
                  color={iconColor.COLOR_DARKGRAY}
                  type={iconType.TRASH_CAN}
                />
              </PlainButtonAsLink>
            )}
          </PlainConfirm>
        )}

        {system && <CardSystemIcon className={styles.system} system={system} />}

        {issuer && <CardIssuerIcon className={styles.issuer} issuer={issuer} />}

        <span className={styles.labelText}>{label}</span>
      </label>
    )
  },
)
